import { useMutation, useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { useToastMessageState } from '../../../../components/ToastMessage/ToastMessageProvider';
import { queryClient } from '../../../../services/MaggieQueryClient';
import { getErrorMessage } from '../../../../services/queryHelpers';
import {
  activateFileIngestionConfig,
  createFileIngestionConfig,
  CreateFileIngestionConfigPayload,
  deleteFileIngestionConfig,
  fetchFileIngestionConfig,
  fetchFileIngestionConfigs,
  updateFileIngestionConfig,
  UpdateFileIngestionConfigPayload,
  validateFileIngestionConfig,
} from '../../../../services/queries/FileIngestionQueries';
import { writeThroughDataType } from '../data-type';

/**
 * Query key for field automation configs
 */
export const FieldAutomationConfigsQueryKey = ['field-automation-configs'] as const;

/**
 * Hook to fetch all field automation configs with optional active filter
 */
export function useFieldAutomationConfigs(activeOnly?: boolean) {
  return useQuery({
    queryKey: activeOnly
      ? [...FieldAutomationConfigsQueryKey, { active: activeOnly }]
      : FieldAutomationConfigsQueryKey,
    queryFn: () => fetchFileIngestionConfigs(activeOnly),
  });
}

/**
 * Hook to fetch a single field automation config by ID
 */
export function useFieldAutomationConfig(id: number | null) {
  return useQuery({
    queryKey: [...FieldAutomationConfigsQueryKey, id],
    queryFn: () => fetchFileIngestionConfig(id!),
    enabled: id !== null,
  });
}

/**
 * Hook to create a new field automation config
 */
export function useCreateFieldAutomationConfig() {
  const { pushSuccessToast, pushErrorToast } = useToastMessageState();

  return useMutation({
    // Write the data type through to the legacy fields the runner keys off (rowStructure,
    // integrationDataDomain) at the save chokepoint, so every caller — not just the form —
    // persists legacy fields that agree with dataType.
    mutationFn: (payload: CreateFileIngestionConfigPayload) =>
      createFileIngestionConfig({ ...payload, config: writeThroughDataType(payload.config) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FieldAutomationConfigsQueryKey });
      pushSuccessToast({ message: 'Field automation configuration created successfully' });
    },
    onError: (error) => {
      Sentry.logger.error(error.message);
      pushErrorToast({
        message: getErrorMessage(error, 'Failed to create configuration'),
      });
    },
  });
}

/**
 * Hook to update an existing field automation config
 */
export function useUpdateFieldAutomationConfig() {
  const { pushSuccessToast, pushErrorToast } = useToastMessageState();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: UpdateFileIngestionConfigPayload }) =>
      updateFileIngestionConfig(
        id,
        payload.config ? { ...payload, config: writeThroughDataType(payload.config) } : payload
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FieldAutomationConfigsQueryKey });
      pushSuccessToast({ message: 'Configuration updated successfully' });
    },
    onError: (error) => {
      Sentry.logger.error(error.message);
      pushErrorToast({
        message: getErrorMessage(error, 'Failed to update configuration'),
      });
    },
  });
}

/**
 * Hook to delete (soft delete) a field automation config
 */
export function useDeleteFieldAutomationConfig() {
  const { pushSuccessToast, pushErrorToast } = useToastMessageState();

  return useMutation({
    mutationFn: (id: number) => deleteFileIngestionConfig(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FieldAutomationConfigsQueryKey });
      pushSuccessToast({ message: 'Configuration deleted successfully' });
    },
    onError: (error) => {
      Sentry.logger.error(error.message);
      pushErrorToast({
        message: getErrorMessage(error, 'Failed to delete configuration'),
      });
    },
  });
}

/**
 * Hook to activate a field automation config
 */
export function useActivateFieldAutomationConfig() {
  const { pushSuccessToast, pushErrorToast } = useToastMessageState();

  return useMutation({
    mutationFn: (id: number) => activateFileIngestionConfig(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FieldAutomationConfigsQueryKey });
      pushSuccessToast({ message: 'Configuration activated successfully' });
    },
    onError: (error) => {
      Sentry.logger.error(error.message);
      pushErrorToast({
        message: getErrorMessage(error, 'Failed to activate configuration'),
      });
    },
  });
}

/**
 * Hook to validate a field automation config
 */
export function useValidateFieldAutomationConfig() {
  const { pushErrorToast } = useToastMessageState();

  return useMutation({
    mutationFn: (id: number) => validateFileIngestionConfig(id),
    onError: (error) => {
      Sentry.logger.error(error.message);
      pushErrorToast({
        message: getErrorMessage(error, 'Failed to validate configuration'),
      });
    },
  });
}
